import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../services/auth';
import { PageHeader, Button, Badge, Tabs, Panel } from '../components/design';
import ServerLogs from '../components/ServerLogs';
import { getActionDefinitions } from '../data/actionDefinitions';
import OverviewTab from './tabs/OverviewTab';
import MetricsTab from './tabs/MetricsTab';
import AlertsTab from './tabs/AlertsTab';
import ServicesTab from './tabs/ServicesTab';
import ConfigTab from './tabs/ConfigTab';
import ActionsTab from './tabs/ActionsTab';
import TroubleshootTab from './tabs/TroubleshootTab';
import AiAnalysisTab from './tabs/AiAnalysisTab';
import SshTerminalTab from './tabs/SshTerminalTab';
import ServerVulnerabilitiesTab from './tabs/ServerVulnerabilitiesTab';
import AnomaliesTab from './tabs/AnomaliesTab';

const CORE_API = '/api';

const STATUS_TONE = {
  online: 'green', reachable: 'green',
  offline: 'red', unreachable: 'red', isolated: 'red',
  pending: 'amber', rebooting: 'amber',
  installing: 'blue',
};

const STATUS_LABEL = {
  online: 'Online', reachable: 'Reachable',
  offline: 'Offline', unreachable: 'Unreachable',
  pending: 'Pending', installing: 'Installing',
  isolated: 'Isolated', rebooting: 'Rebooting',
};

export default function ServerDetail({ serverId }) {
  const { user } = useAuth();
  const ROLE_LEVEL = { viewer: 0, engineer: 1, operator: 2, admin: 3, super_admin: 4 };
  const userLevel = ROLE_LEVEL[user?.role] ?? -1;
  const canEdit = userLevel >= ROLE_LEVEL.engineer;
  const canAdmin = userLevel >= ROLE_LEVEL.admin;
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('overview');
  const [showActionConfirm, setShowActionConfirm] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteHistory, setDeleteHistory] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  const { data: serverData, isLoading: serverLoading, refetch: refetchServer, isFetching: serverFetching } = useQuery({
    queryKey: ['servers', serverId],
    queryFn: () => fetch(`${CORE_API}/servers/${serverId}`).then(res => res.json()),
    refetchInterval: 15000,
  });

  const server = serverData;

  const actionConfirmations = Object.fromEntries(
    getActionDefinitions(server).map(d => [d.action, d.confirm])
  );

  const ip = serverData?.ip_address?.split('/')[0] || serverData?.ip_address || '';

  const { data: cpuData } = useQuery({
    queryKey: ['metrics', 'cpu', serverId],
    queryFn: async () => {
      if (!ip) return null;
      const query = `100 - (avg by(instance) (rate(node_cpu_seconds_total{instance=~"${ip}:.*",mode="idle"}[5m]))) * 100`;
      const res = await fetch(`${CORE_API}/metrics/prometheus/range?query=${encodeURIComponent(query)}&hours=1&step=60s`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!serverData?.ip_address && (activeTab === 'overview' || activeTab === 'metrics'),
    refetchInterval: 60000,
    retry: 0,
  });

  const { data: memData } = useQuery({
    queryKey: ['metrics', 'memory', serverId],
    queryFn: async () => {
      if (!ip) return null;
      const query = `(1 - (node_memory_MemAvailable_bytes{instance=~"${ip}:.*"} / node_memory_MemTotal_bytes{instance=~"${ip}:.*"})) * 100`;
      const res = await fetch(`${CORE_API}/metrics/prometheus/range?query=${encodeURIComponent(query)}&hours=1&step=60s`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!serverData?.ip_address && (activeTab === 'overview' || activeTab === 'metrics'),
    refetchInterval: 60000,
    retry: 0,
  });

  const { data: diskData } = useQuery({
    queryKey: ['metrics', 'disk', serverId],
    queryFn: async () => {
      if (!ip) return null;
      const query = `(1 - (node_filesystem_avail_bytes{instance=~"${ip}:.*",fstype!~"tmpfs|squashfs"} / node_filesystem_size_bytes{instance=~"${ip}:.*",fstype!~"tmpfs|squashfs"})) * 100`;
      const res = await fetch(`${CORE_API}/metrics/prometheus/range?query=${encodeURIComponent(query)}&hours=1&step=60s`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!serverData?.ip_address && (activeTab === 'overview' || activeTab === 'metrics'),
    refetchInterval: 60000,
    retry: 0,
  });

  const { data: netData } = useQuery({
    queryKey: ['metrics', 'network', serverId],
    queryFn: async () => {
      if (!ip) return null;
      const rxQ = `sum(rate(node_network_receive_bytes_total{instance=~"${ip}:.*"}[5m])) * 8`;
      const txQ = `sum(rate(node_network_transmit_bytes_total{instance=~"${ip}:.*"}[5m])) * 8`;
      const [rx, tx] = await Promise.all([
        fetch(`${CORE_API}/metrics/prometheus/range?query=${encodeURIComponent(rxQ)}&hours=24&step=3600s`).then(r => r.ok ? r.json() : null),
        fetch(`${CORE_API}/metrics/prometheus/range?query=${encodeURIComponent(txQ)}&hours=24&step=3600s`).then(r => r.ok ? r.json() : null),
      ]);
      return { rx, tx };
    },
    enabled: !!serverData?.ip_address && activeTab === 'metrics',
    refetchInterval: 60000,
    retry: 0,
  });

  const checkHealthMutation = useMutation({
    mutationFn: () => fetch(`${CORE_API}/servers/${serverId}/check`, { method: 'POST' }).then(res => res.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => fetch(`${CORE_API}/servers/${serverId}?delete_history=${deleteHistory}`, { method: 'DELETE' }).then(res => res.json()),
    onSuccess: () => {
      window.location.hash = 'servers';
    },
  });

  const runAction = async (action, serviceName = null) => {
    const confirm = actionConfirmations[action];
    if (confirm && (confirm.requireText || confirm.inputPrompt)) {
      setConfirmText('');
      setShowActionConfirm({ action, serviceName, ...confirm });
      return;
    }
    setActionLoading(true);
    setActionResult(null);
    try {
      const res = await fetch(`${CORE_API}/servers/${serverId}/actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, service_name: serviceName }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || `HTTP ${res.status}`);
      const data = await res.json();
      setActionResult(data);
      queryClient.invalidateQueries({ queryKey: ['servers', serverId] });
    } catch (e) {
      setActionResult({ error: e.message });
    }
    setActionLoading(false);
  };

  const confirmAction = async () => {
    if (!showActionConfirm) return;
    if (showActionConfirm.requireText && confirmText !== showActionConfirm.requireText) {
      setActionResult({ error: 'Confirmation text does not match' });
      return;
    }
    const svc = showActionConfirm.inputPrompt ? confirmText : showActionConfirm.serviceName;
    setShowActionConfirm(null);
    setConfirmText('');
    await runAction(showActionConfirm.action, svc);
  };

  const runTroubleshoot = async () => {
    try {
      const res = await fetch(`${CORE_API}/servers/${serverId}/troubleshoot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.success) {
        let agentLines = '';
        if (data.agent_status) {
          for (const [key, val] of Object.entries(data.agent_status)) {
            if (typeof val === 'object' && val.active !== undefined) {
              agentLines += `  **${key}**: ${val.active ? 'Active' : 'Inactive'} (confidence: ${val.confidence}%, layers: ${val.layers_passed?.join(', ') || 'none'})\n`;
            }
          }
        }
        const result = {
          mode: 'TROUBLESHOOT',
          diagnostics: data.diagnostics,
          result: `## Server Troubleshoot: ${serverData?.name}\n\n` +
            `**Uptime:** ${data.diagnostics?.uptime || 'N/A'}\n\n` +
            `**Disk:** ${data.diagnostics?.disk || 'N/A'}\n\n` +
            `**Memory:** ${data.diagnostics?.memory || 'N/A'}\n\n` +
            `### Agent Status\n${agentLines || 'No agent data available'}`,
          timestamp: new Date().toISOString(),
        };
        queryClient.invalidateQueries({ queryKey: ['servers', serverId] });
        return result;
      } else {
        return { error: data.detail || 'Troubleshoot failed' };
      }
    } catch (e) {
      return { error: e.message };
    }
  };

  const cpuSeries = cpuData?.data?.result?.[0]?.values || [];
  const memSeries = memData?.data?.result?.[0]?.values || [];
  const diskSeries = diskData?.data?.result?.[0]?.values || [];
  const netRxSeries = netData?.rx?.data?.result?.[0]?.values || [];
  const netTxSeries = netData?.tx?.data?.result?.[0]?.values || [];

  const cpuValue = cpuSeries.length > 0 ? parseFloat(cpuSeries[cpuSeries.length - 1][1]).toFixed(1) : null;
  const memValue = memSeries.length > 0 ? parseFloat(memSeries[memSeries.length - 1][1]).toFixed(1) : null;
  const diskValue = diskSeries.length > 0 ? parseFloat(diskSeries[diskSeries.length - 1][1]).toFixed(1) : null;

  const safeCpuValue = cpuValue && !isNaN(cpuValue) ? cpuValue : null;
  const safeMemValue = memValue && !isNaN(memValue) ? memValue : null;
  const safeDiskValue = diskValue && !isNaN(diskValue) ? diskValue : null;

  const handleDelete = () => {
    setConfirmText('');
    setDeleteHistory(false);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (confirmText === server?.name) {
      deleteMutation.mutate();
      setShowDeleteConfirm(false);
      setConfirmText('');
      setDeleteHistory(false);
    }
  };

  const statusBadge = (status) => (
    <Badge tone={STATUS_TONE[status] || 'gray'}>{STATUS_LABEL[status] || status?.toUpperCase()}</Badge>
  );

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'metrics', label: 'Metrics' },
    { id: 'logs', label: 'Logs' },
    { id: 'alerts', label: 'Alerts' },
    { id: 'vulnerabilities', label: 'Vulnerabilities' },
    { id: 'services', label: 'Services' },
    { id: 'config', label: 'Config' },
    { id: 'actions', label: 'Actions' },
    { id: 'troubleshoot', label: 'Troubleshoot' },
    { id: 'ai', label: 'AI Analysis' },
    { id: 'ssh', label: 'SSH Terminal' },
    { id: 'anomalies', label: 'Anomalies' },
  ];

  if (serverLoading) {
    return <div className="muted" style={{ textAlign: 'center', padding: '80px 0' }}>Loading server details...</div>;
  }

  if (!server) {
    return (
      <div className="muted" style={{ textAlign: 'center', padding: '80px 0' }}>
        Server not found. <a href="#servers" style={{ color: 'var(--accent-1)' }}>Back to servers list</a>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        crumbs={[{ label: 'Servers', onClick: () => { window.location.hash = 'servers'; } }]}
        title={server.name}
        subtitle={`${server.ip_address} · ${server.os_type}${server.os_version ? ` ${server.os_version}` : ''} · ${server.hostname || 'No hostname'}`}
        actions={
          <div className="flex items-center gap-3 flex-wrap">
            {statusBadge(server.status)}
            <Button variant="ghost" onClick={() => refetchServer()} disabled={serverFetching} icon="refresh-cw">
              Refresh
            </Button>
            <Button variant="ghost" onClick={() => checkHealthMutation.mutate()} disabled={checkHealthMutation.isPending} icon="check-circle">
              {checkHealthMutation.isPending ? 'Checking...' : 'Check Health'}
            </Button>
            {canAdmin && (
              <Button variant="danger" onClick={handleDelete} disabled={deleteMutation.isPending} icon="trash-2">
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            )}
          </div>
        }
      />

      <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />

      {/* Action Confirmation Modal */}
      {showActionConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }} onClick={() => { setShowActionConfirm(null); setConfirmText(''); }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--glass-border)', borderRadius: 14, padding: 24, maxWidth: 448, width: '100%', margin: 16 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: showActionConfirm.type === 'danger' ? 'var(--danger)' : 'var(--warning)' }}>
              {showActionConfirm.title}
            </h3>
            <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>{showActionConfirm.message}</p>
            {showActionConfirm.requireText && (
              <div className="field">
                <label>{showActionConfirm.textPrompt}</label>
                <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && confirmText === showActionConfirm.requireText) confirmAction(); }} autoFocus />
              </div>
            )}
            {showActionConfirm.inputPrompt && !showActionConfirm.requireText && (
              <div className="field">
                <label>{showActionConfirm.inputPrompt}</label>
                <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && confirmText.trim()) confirmAction(); }} autoFocus />
              </div>
            )}
            <div className="flex items-center gap-3">
              <Button variant={showActionConfirm.type === 'danger' ? 'danger' : 'primary'} onClick={confirmAction} disabled={showActionConfirm.requireText && confirmText !== showActionConfirm.requireText || showActionConfirm.inputPrompt && !confirmText.trim()} style={{ flex: 1 }}>
                {showActionConfirm.inputPrompt ? 'Restart' : 'Confirm'}
              </Button>
              <Button variant="ghost" onClick={() => { setShowActionConfirm(null); setConfirmText(''); }} style={{ flex: 1 }}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid rgba(248,113,113,0.35)', borderRadius: 14, padding: 24, maxWidth: 448, width: '100%', margin: 16 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--danger)', marginBottom: 8 }}>Delete Server</h3>
            <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
              This will permanently delete &quot;{server.name}&quot; from SYNOTI. All exporters, tasks, and configurations will be removed.
            </p>
            <label className="check-row" style={{ marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={deleteHistory} onChange={e => setDeleteHistory(e.target.checked)} />
              Also delete all monitoring history (incidents, alerts, logs, reports)
            </label>
            <div className="field">
              <label>Type &quot;{server.name}&quot; to confirm deletion</label>
              <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)} />
            </div>
            <div className="flex items-center gap-3">
              <Button variant="danger" onClick={confirmDelete} disabled={confirmText !== server.name || deleteMutation.isPending} style={{ flex: 1 }}>
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
              <Button variant="ghost" onClick={() => { setShowDeleteConfirm(false); setConfirmText(''); }} style={{ flex: 1 }}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab server={server}
          cpuSeries={cpuSeries} cpuValue={safeCpuValue}
          memSeries={memSeries} memValue={safeMemValue}
          diskSeries={diskSeries} diskValue={safeDiskValue} />
      )}

      {activeTab === 'metrics' && (
        <MetricsTab server={server} serverId={serverId}
          cpuSeries={cpuSeries} cpuValue={safeCpuValue}
          memSeries={memSeries} memValue={safeMemValue}
          diskSeries={diskSeries} diskValue={safeDiskValue}
          netRxSeries={netRxSeries} netTxSeries={netTxSeries} />
      )}

      {activeTab === 'logs' && (
        <Panel title="Server Logs">
          <ServerLogs server={server} />
        </Panel>
      )}

      {activeTab === 'alerts' && (
        <AlertsTab serverId={serverId} canEdit={canEdit} />
      )}

      {activeTab === 'vulnerabilities' && (
        <ServerVulnerabilitiesTab serverId={serverId} />
      )}

      {activeTab === 'services' && (
        <ServicesTab serverId={serverId} canEdit={canEdit} />
      )}

      {activeTab === 'config' && (
        <ConfigTab serverId={serverId} server={server} canEdit={canEdit} />
      )}

      {activeTab === 'actions' && (
        <ActionsTab serverId={serverId} server={server} canEdit={canEdit}
          runAction={runAction} actionLoading={actionLoading} actionResult={actionResult} />
      )}

      {activeTab === 'troubleshoot' && (
        <TroubleshootTab serverId={serverId} server={server} queryClient={queryClient} />
      )}

      {activeTab === 'ai' && (
        <AiAnalysisTab serverId={serverId} server={server} runTroubleshoot={runTroubleshoot} />
      )}

      {activeTab === 'ssh' && (
        <SshTerminalTab serverId={serverId} server={server} />
      )}

      {activeTab === 'anomalies' && (
        <AnomaliesTab serverId={serverId} />
      )}
    </div>
  );
}
